import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCreateTenant, useEnvironment, useSaveTenantBilling } from '../../hooks';
import type { BillingCycle } from '../../types';
import { CYCLE_LABEL, isoToday } from '../Invoices/format';
import { ENVIRONMENT_LABELS } from '../../constants';
import styles from './NewTenant.module.scss';

export const TENANT_KEY = /^[a-z][a-z0-9]{2,29}$/;

const keyHint = (key: string): string | null => {
  if (!key) return null;
  if (key.length < 3) return 'At least 3 characters.';
  if (key.length > 30) return 'At most 30 characters.';
  if (!/^[a-z]/.test(key)) return 'Must start with a letter.';
  if (!/^[a-z0-9]+$/.test(key))
    return 'Lowercase letters and digits only: it becomes S3 bucket names (no underscores) and the dev onboard workflow rejects hyphens.';
  return null;
};

export const NewTenant = () => {
  const navigate = useNavigate();
  const { environment, isProd } = useEnvironment();
  const create = useCreateTenant();
  const [key, setKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [domains, setDomains] = useState('');
  const [notes, setNotes] = useState('');
  const [sftp, setSftp] = useState(false);
  const [zonalUser, setZonalUser] = useState('');
  const [zonalKey, setZonalKey] = useState('');
  const [confirm, setConfirm] = useState('');
  const [cycle, setCycle] = useState<BillingCycle>('quarterly');
  const [cycleStart, setCycleStart] = useState(isoToday());
  const [terms, setTerms] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const saveBilling = useSaveTenantBilling(key);

  const hint = keyHint(key);
  const keyOk = TENANT_KEY.test(key);
  const sftpOk = !sftp || /^zonal-[a-z0-9][a-z0-9_-]{0,30}$/.test(zonalUser.trim());
  const confirmOk = !isProd || confirm.trim() === key;
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail.trim());
  const domainList = domains
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);
  const domainsOk = domainList.every((item) => /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/.test(item));
  const canSubmit = keyOk && emailOk && domainsOk && sftpOk && confirmOk && !create.isLoading;
  const envLabel = ENVIRONMENT_LABELS[environment];
  const error = create.error as { message?: string } | null;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    create.mutate(
      {
        name: key,
        display_name: displayName.trim() || null,
        notes: notes.trim() || null,
        admin_email: adminEmail.trim().toLowerCase(),
        domains: domainList,
        provision_sftp: sftp,
        zonal_tenant_ssh_username: sftp ? zonalUser.trim() : null,
        zonal_tenant_ssh_public_key: sftp ? zonalKey.trim() || null : null,
      },
      {
        onSuccess: ({ data }) => {
          const go = () =>
            navigate(`/tenants/${encodeURIComponent(data.tenant.name)}/provisioning`);
          saveBilling.mutate(
            {
              cycle,
              cycle_start: cycleStart,
              timing: 'advance',
              payment_terms_days: terms === '' ? null : Number(terms),
              account_number: accountNumber.trim() || null,
              billing_name: displayName.trim() || null,
              billing_address: billingAddress.trim() || null,
              customer_address: null,
              po_number: null,
              vat_rate: null,
              notes: null,
            },
            { onSettled: go },
          );
        },
      },
    );
  };

  return (
    <div className={styles.page}>
      <nav className={styles.crumbs} aria-label="Breadcrumb">
        <Link to="/tenants">Tenants</Link>
        <span aria-hidden="true">/</span>
        <span>New tenant</span>
      </nav>
      <header className={styles.header}>
        <h1 className={styles.title}>New tenant</h1>
        <p className={styles.subtitle}>
          Creates the WorkOS organisation and invites the first admin, then runs{' '}
          <code>deploy-tenant-{environment}.yml</code> (infrastructure) and{' '}
          <code>onboard-tenant-{environment}.yaml</code> (schema and migrations) in{' '}
          <strong>{envLabel}</strong>. The database role and the Nexus read grant follow as a
          checklist on the tenant page.
        </p>
      </header>
      <form className={styles.layout} onSubmit={submit} aria-label="New tenant">
        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>Identity</h2>
          <label className={styles.field}>
            <span>Tenant key</span>
            <input
              value={key}
              onChange={(event) => setKey(event.target.value.toLowerCase())}
              placeholder="e.g. heartwood"
              autoComplete="off"
              spellCheck={false}
              required
              aria-invalid={Boolean(hint)}
            />
            <small className={hint ? styles.bad : undefined}>
              {hint ??
                'Lowercase letters and digits, 3 to 30 characters. It cannot be changed afterwards.'}
            </small>
          </label>
          <label className={styles.field}>
            <span>Display name</span>
            <input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="e.g. Heartwood Collection"
            />
            <small>Shown in Nexus only. Defaults to the key.</small>
          </label>
          <label className={styles.field}>
            <span>Notes</span>
            <textarea
              rows={3}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Who asked for it, contract reference, anything the next person needs"
            />
          </label>
        </section>

        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>WorkOS</h2>
          <label className={styles.field}>
            <span>First admin email</span>
            <input
              type="email"
              value={adminEmail}
              onChange={(event) => setAdminEmail(event.target.value)}
              placeholder="ops@tenant.example"
              required
              aria-invalid={Boolean(adminEmail) && !emailOk}
            />
            <small>
              Invited to the new organisation with the <code>admin</code> role before anything is
              provisioned. They get an email from WorkOS and can log in to admin-ui once the schema
              exists.
            </small>
          </label>
          <label className={styles.field}>
            <span>Email domains (optional)</span>
            <input
              value={domains}
              onChange={(event) => setDomains(event.target.value)}
              placeholder="tenant.example, tenant.co.uk"
              aria-invalid={Boolean(domains) && !domainsOk}
            />
            <small className={domains && !domainsOk ? styles.bad : undefined}>
              Comma separated, recorded as verified on the organisation for SSO. Leave empty to add
              later in WorkOS.
            </small>
          </label>
        </section>

        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>Billing</h2>
          <div className={styles.row}>
            <label className={styles.field}>
              <span>Billing cycle</span>
              <select
                value={cycle}
                onChange={(event) => setCycle(event.target.value as BillingCycle)}
              >
                {(Object.keys(CYCLE_LABEL) as BillingCycle[]).map((option) => (
                  <option key={option} value={option}>
                    {CYCLE_LABEL[option]}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Cycle starts</span>
              <input
                type="date"
                value={cycleStart}
                onChange={(event) => setCycleStart(event.target.value)}
              />
              <small>Invoice periods are anchored on this date.</small>
            </label>
          </div>
          <div className={styles.row}>
            <label className={styles.field}>
              <span>Account number</span>
              <input
                value={accountNumber}
                onChange={(event) => setAccountNumber(event.target.value)}
                placeholder="from accounts, e.g. 44851"
              />
            </label>
            <label className={styles.field}>
              <span>Payment terms (days)</span>
              <input
                type="number"
                min={0}
                value={terms}
                onChange={(event) => setTerms(event.target.value)}
                placeholder="company default"
              />
            </label>
          </div>
          <label className={styles.field}>
            <span>Billing address</span>
            <textarea
              rows={3}
              value={billingAddress}
              onChange={(event) => setBillingAddress(event.target.value)}
              placeholder={'The Brewery\nBlandford St. Mary\nDorset DT11 9LS'}
            />
            <small>Printed on invoices. Can be changed later on the Billing tab.</small>
          </label>
        </section>

        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>Zonal SFTP</h2>
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={sftp}
              onChange={(event) => setSftp(event.target.checked)}
            />
            <span>Provision an SFTP directory for this tenant</span>
          </label>
          <small className={styles.muted}>
            Only for tenants that exchange files with Zonal. Creates the user and directory on the
            SFTP host and the DynamoDB mapping.
          </small>
          {sftp ? (
            <>
              <label className={styles.field}>
                <span>SSH username</span>
                <input
                  value={zonalUser}
                  onChange={(event) => setZonalUser(event.target.value)}
                  placeholder="zonal-heartwood"
                  required
                />
                <small className={!sftpOk && zonalUser ? styles.bad : undefined}>
                  Must start with <code>zonal-</code>.
                </small>
              </label>
              <label className={styles.field}>
                <span>SSH public key (optional)</span>
                <textarea
                  rows={3}
                  value={zonalKey}
                  onChange={(event) => setZonalKey(event.target.value)}
                  placeholder="ssh-ed25519 AAAA… zonal@tenant"
                />
                <small>Leave empty to add later.</small>
              </label>
            </>
          ) : null}
        </section>

        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>What gets created in {envLabel}</h2>
          <ul className={styles.created}>
            <li>
              <code>
                {key || '<key>'}-openr-tf-state-{environment}
              </code>{' '}
              Terraform state bucket
            </li>
            <li>
              <code>
                {key || '<key>'}-product-images-{environment}
              </code>
              ,{' '}
              <code>
                {key || '<key>'}-allergen-export-{environment}
              </code>
              ,{' '}
              <code>
                {key || '<key>'}-payload-storage-{environment}
              </code>
              ,{' '}
              <code>
                {key || '<key>'}-sync-response-bucket-{environment}
              </code>
            </li>
            <li>
              KMS key <code>alias/{key || '<key>'}-kms-key</code> and secret{' '}
              <code>tenant_{key || '<key>'}_config</code>
            </li>
            <li>
              IAM policy{' '}
              <code>
                {key || '<key>'}-rds-s3-policy-{environment}
              </code>
            </li>
          </ul>
          <p className={styles.muted}>
            Also created: WorkOS organisation <code>{key || '<key>'}</code> with{' '}
            <code>{adminEmail || '<admin email>'}</code> invited as admin, then schema{' '}
            <code>{key || '<key>'}</code> with migrations. Not created here: the per-tenant database
            role and the Nexus read grant. Those are the checklist after the run.
          </p>
          {isProd ? (
            <label className={styles.field}>
              <span className={styles.bad}>
                This is production. Type the tenant key to confirm.
              </span>
              <input
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                placeholder={key || 'tenant key'}
                autoComplete="off"
              />
            </label>
          ) : null}
          {error ? (
            <p className={styles.error} role="alert">
              {error.message ?? 'Could not start provisioning'}
            </p>
          ) : null}
          <div className={styles.actions}>
            <Link to="/tenants" className={styles.ghost}>
              Cancel
            </Link>
            <button type="submit" className={styles.primary} disabled={!canSubmit}>
              {create.isLoading ? 'Starting…' : `Create tenant in ${envLabel}`}
            </button>
          </div>
        </section>
      </form>
    </div>
  );
};
